"use client";

import React, { useMemo } from "react";
import Slider from "react-slick";
import styled from "styled-components";

import {
  Progress,
  ProgressColor,
  Text,
  TextColor,
  TextSize,
  TextVariant,
  TextWeight,
} from "../../components";
import { useMatchBreakpoints } from "../../hooks";
import type { Order, OrderProduct } from "../../types/order.type";
import { parseProductName } from "../../utils";
import { ProductListItem } from "../ProductListItem";

type OrderDetailsProps = {
  order: Order | null;
};

const OrderDetails: React.FC<OrderDetailsProps> = ({ order }) => {
  const { isXs, isSm, isMd } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd;

  console.log(order);

  // Memoize slider settings
  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
    }),
    [],
  );

  if (!order) {
    return (
      <div className="flex items-center justify-center h-full">
        <Progress color={ProgressColor.GRAYSCALE_400} />
      </div>
    );
  }

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "Brak danych";
    return new Date(dateString).toLocaleDateString("pl-PL");
  };

  const getCurrentOrderState = (): string => {
    if (!order.history?.current) return "Brak danych";
    return order.history.current.ostateName || "Brak danych";
  };

  const getTrackingNumber = (): string => {
    if (order.shipping && order.shipping.length > 0) {
      return order.shipping[0]?.trackingNumber || "Brak numeru";
    }
    return "Brak numeru";
  };

  const getUniqueOrderHistory = () => {
    const uniqueStatuses: { [key: string]: any } = {};

    // Process all history entries to find the latest date for each status
    Object.entries(order.history).forEach(([key, historyItem]) => {
      const statusName = historyItem.ostateName;
      const currentDate = new Date(historyItem.dateAdd);

      if (
        !uniqueStatuses[statusName] ||
        new Date(uniqueStatuses[statusName].dateAdd) < currentDate
      ) {
        uniqueStatuses[statusName] = { key, ...historyItem };
      }
    });

    // Convert back to array and sort by date (newest first)
    return Object.values(uniqueStatuses).sort(
      (a: any, b: any) =>
        new Date(b.dateAdd).getTime() - new Date(a.dateAdd).getTime(),
    );
  };

  const productsArray: OrderProduct[] = Object.values(order.products || {});

  const renderProducts = (products: OrderProduct[]) => {
    if (products) {
      const parsedProducts = products.map((product: any) => {
        console.log(product.productName);
        const { name, size, color } = parseProductName(product.productName);
        console.log(name);
        return {
          ...product,
          name,
          size,
          color,
        };
      });

      if (isMobile) {
        return (
          <SliderWrapper>
            <Slider {...sliderSettings}>
              {parsedProducts.map((product: any) => (
                <ProductListItem
                  key={product.idOrderDetail}
                  product={product}
                />
              ))}
            </Slider>
          </SliderWrapper>
        );
      }

      return (
        <div className="space-y-3">
          {parsedProducts.map((product: any) => (
            <ProductListItem key={product.idOrderDetail} product={product} />
          ))}
        </div>
      );
    }

    return <Text text="Brak danych o produktach" fontSize={TextSize.M} />;
  };

  return (
    <div>
      <div className="mb-6">
        <Text
          text={`Szczegóły zamówienia: ${order.details?.id}`}
          variant={TextVariant.H5}
          weight={TextWeight.BOLD}
        />
      </div>

      <div className="bg-gray-50 shadow-sm p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <span className="text-sm text-gray-600">Złożone:</span>
            <p className="font-semibold">
              {formatDate(order.details?.orderDate)}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-600">Status:</span>
            <p className="font-semibold">{getCurrentOrderState()}</p>
          </div>
          <div>
            <span className="text-sm text-gray-600">Nr przesyłki:</span>
            <p className="font-semibold text-sm">{getTrackingNumber()}</p>
          </div>
        </div>
      </div>

      {/* Forma dostawy i płatności */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gray-50 shadow-sm p-6 mb-0 md:mb-6 flex flex-col gap-2">
          <Text text="Forma dostawy" weight={TextWeight.BOLD} />
          <Text text={order.details?.shipping[0]?.stateName || "Brak danych"} />
        </div>

        <div className="bg-gray-50 shadow-sm p-6 mb-6 flex flex-col gap-2">
          <Text text="Forma płatności" weight={TextWeight.BOLD} />
          <Text text={order.details?.payment || "Brak danych"} />
        </div>
      </div>

      <div className="bg-gray-50 shadow-sm p-6 mb-6">
        <Text
          text={`Produkty w zamówieniu (${order.productsCount || productsArray.length})`}
          weight={TextWeight.BOLD}
          className="mb-4"
        />

        <div className="space-y-4 mb-12 md:mb-4 -mx-3 md:mx-0">
          {renderProducts(productsArray)}
        </div>

        <div className="flex justify-end">
          <div className="flex flex-col gap-2 w-full md:w-1/2">
            <Text
              text="Wartość zamówienia"
              weight={TextWeight.BOLD}
              className="mb-2"
            />

            <div className="flex justify-between">
              <Text text="Wartość produktów:" fontSize={TextSize.S} />
              <Text
                text={order.subtotals?.products.value}
                fontSize={TextSize.S}
              />
            </div>

            {order.subtotals?.discounts.value && (
              <div className="flex justify-between">
                <Text text="Wartość rabatu:" fontSize={TextSize.S} />
                <Text
                  text={order.subtotals?.discounts.value}
                  fontSize={TextSize.S}
                />
              </div>
            )}

            <div className="flex justify-between">
              <Text text="Koszt dostawy:" fontSize={TextSize.S} />
              <Text
                text={order.subtotals?.shipping.value}
                fontSize={TextSize.S}
              />
            </div>

            <div className="flex justify-between mt-2 border-t pt-4">
              <Text
                text="Do zapłaty:"
                fontSize={TextSize.S}
                weight={TextWeight.BOLD}
              />
              <Text
                text={order.totals?.totalIncludingTax.value}
                fontSize={TextSize.S}
                weight={TextWeight.BOLD}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Adresy */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Adres dostawy */}
        <div
          className={`bg-gray-50 shadow-sm p-6 ${order.addresses?.delivery?.id !== order.addresses?.invoice?.id ? "mb-0 md:mb-6" : "mb-6"} flex flex-col gap-2`}
        >
          <Text text="Adres dostawy" weight={TextWeight.BOLD} />

          <div className="flex flex-col gap-1">
            <Text
              text={order.addresses?.delivery?.company}
              fontSize={TextSize.S}
            />
            <Text
              text={`${order.addresses?.delivery?.firstname} ${order.addresses?.delivery?.lastname}`}
              fontSize={TextSize.S}
            />
            <Text
              text={`${order.addresses?.delivery?.address1} ${order.addresses?.delivery?.address2}`}
              fontSize={TextSize.S}
            />
            <Text
              text={`${order.addresses?.delivery?.postcode} ${order.addresses?.delivery?.city}`}
              fontSize={TextSize.S}
            />
            <Text
              text={`${order.addresses?.delivery?.phone || order.addresses?.delivery?.phoneMobile}`}
              fontSize={TextSize.S}
            />
          </div>
        </div>

        {/* Adres faktury */}
        {order.addresses?.delivery?.id !== order.addresses?.invoice?.id && (
          <div className="bg-gray-50 shadow-sm p-6 mb-6 flex flex-col gap-2">
            <Text text="Adres faktury" weight={TextWeight.BOLD} />

            <div className="flex flex-col gap-1">
              <Text
                text={order.addresses?.invoice?.company}
                fontSize={TextSize.S}
              />
              <Text
                text={`${order.addresses?.invoice?.firstname} ${order.addresses?.invoice?.lastname}`}
                fontSize={TextSize.S}
              />
              <Text
                text={`${order.addresses?.invoice?.address1} ${order.addresses?.invoice?.address2}`}
                fontSize={TextSize.S}
              />
              <Text
                text={`${order.addresses?.invoice?.postcode} ${order.addresses?.invoice?.city}`}
                fontSize={TextSize.S}
              />
              <Text
                text={`${order.addresses?.invoice?.phone || order.addresses?.invoice?.phoneMobile}`}
                fontSize={TextSize.S}
              />
            </div>
          </div>
        )}
      </div>

      {/* Zmiana statusów zamówienia */}
      <div className="bg-gray-50 shadow-sm p-6 mb-6 flex flex-col gap-2">
        <Text text="Śledź zamówienie krok po kroku" weight={TextWeight.BOLD} />

        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="py-4 text-left">
                <Text
                  text="Data i godzina"
                  fontSize={TextSize.S}
                  weight={TextWeight.SEMIBOLD}
                  color={TextColor.GRAYSCALE_600}
                />
              </th>
              <th className="py-4 text-left">
                <Text
                  text="Status zamówienia"
                  fontSize={TextSize.S}
                  weight={TextWeight.SEMIBOLD}
                  color={TextColor.GRAYSCALE_600}
                />
              </th>
              <th className="py-4 text-left w-4">
                {/* Status indicator column */}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {getUniqueOrderHistory().map((historyItem, index) => {
              const isLatest = index === 0;
              return (
                <tr
                  key={historyItem.key}
                  className={`hover:bg-gray-50 transition-colors ${isLatest ? "bg-blue-50" : ""}`}
                >
                  <td className="px-1 py-4">
                    <Text
                      text={formatDate(historyItem.dateAdd)}
                      fontSize={TextSize.S}
                      color={
                        isLatest
                          ? TextColor.GRAYSCALE_900
                          : TextColor.GRAYSCALE_600
                      }
                      weight={
                        isLatest ? TextWeight.SEMIBOLD : TextWeight.NORMAL
                      }
                    />
                  </td>
                  <td className="py-4">
                    <Text
                      text={historyItem.ostateName}
                      fontSize={TextSize.S}
                      weight={isLatest ? TextWeight.BOLD : TextWeight.SEMIBOLD}
                      color={
                        isLatest
                          ? TextColor.GRAYSCALE_900
                          : TextColor.GRAYSCALE_600
                      }
                    />
                  </td>
                  <td className="px-2 py-4">
                    <div className="flex items-center justify-center">
                      <div
                        className={`w-3 h-3 rounded-full ${isLatest ? "ring-2 ring-blue-200 bg-blue-500" : ""}`}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SliderWrapper = styled.div`
  .slick-track {
    display: flex;
    gap: 10px;
  }

  .slick-list {
    margin-left: -10px;
  }

  .slick-dots {
    bottom: -30px;
    li {
      button:before {
        font-size: 30px;
        color: ${({ theme }) => theme.colors.BLACK};
      }
    }
  }
`;

export default OrderDetails;
